
import { defineComponent } from "vue";
import { getModule } from "vuex-module-decorators";
import { ElMessage } from 'element-plus'

import {mdiFullscreen, mdiChevronRight, mdiDotsVertical, mdiPencil, mdiDelete,
mdiDragHorizontalVariant, mdiMagnify, mdiFormatListText, mdiCrosshairsGps, 
mdiInformationVariant, mdiVideoImage, mdiBookOpen, mdiSpotlight, mdiMap, mdiWeb,
mdiViewCarousel, mdiViewSplitVertical, mdiTextBoxPlus, mdiStarBox, mdiFaceMask,
mdiHandWash, mdiAccountGroup, mdiSocialDistance6Feet, mdiOpenInNew } from '@mdi/js';

// Layout Editors --
import LiveTemplatePreview from './LiveTemplatePreview.vue';
import DetailedInformationalLayoutEditor from '../signage-layout-editors/DetailedInformationalLayoutEditor.vue';
import DirectoryLayoutEditor from '../signage-layout-editors/DirectoryLayoutEditor.vue';
import FeatureLayoutEditor from '../signage-layout-editors/FeatureLayoutEditor.vue';
import FeatureTwoLayoutEditor from '../signage-layout-editors/FeatureTwoLayoutEditor.vue';
import InformationalLayoutEditor from '../signage-layout-editors/InformationalLayoutEditor.vue';
import LargeHeadlineInformationalLayoutEditor from '../signage-layout-editors/LargeHeadlineInformationalLayoutEditor.vue';
import MediaLayoutEditor from '../signage-layout-editors/MediaLayoutEditor.vue';
import MultiItemMenuLayoutEditor from '../signage-layout-editors/MultiItemMenuLayoutEditor.vue';
import SingleItemMenuLayoutEditor from '../signage-layout-editors/SingleItemMenuLayoutEditor.vue';
import SpotlightLayoutEditor from '../signage-layout-editors/SpotlightLayoutEditor.vue';
import StaticWebUrlLayoutEditor from '../signage-layout-editors/StaticWebUrlLayoutEditor.vue';
import WayfindingLayoutEditor from '../signage-layout-editors/WayfindingLayoutEditor.vue';
import WeatherThreeDayLayoutEditor from '../signage-layout-editors/WeatherThreeDayLayoutEditor.vue';
import WeatherTodayLayoutEditor from '../signage-layout-editors/WeatherTodayLayoutEditor.vue';
import CanvaLayoutEditor from '../signage-layout-editors/CanvaLayoutEditor.vue';
import TwitterLayoutEditor from '../signage-layout-editors/TwitterLayoutEditor.vue';
import GoogleSlidesImportLayoutEditor from '../signage-layout-editors/GoogleSlidesImportLayoutEditor.vue';
import MediaQuadrantsLayoutEditor from '../signage-layout-editors/MediaQuadrantsLayoutEditor.vue';
import YoutubeUrlLayoutEditor from '../signage-layout-editors/YoutubeUrlLayoutEditor.vue';

// Covid Layout Editors --
import CovidMaskLayoutEditor from '../signage-layout-editors/covid/CovidMaskLayoutEditor.vue';
import OccupancyHealth from '../signage-layout-editors/covid/CovidOccupancyLayoutEditor.vue';
import OccupancyGeneral from '../signage-layout-editors/covid/CovidOccupancyTwoLayoutEditor.vue';
import HealthTips from '../signage-layout-editors/covid/CovidSafetyLayoutEditor.vue';
import NonEditableLayoutEditor from '../signage-layout-editors/NonEditableLayoutEditor.vue';

// Components ---
import Icon from '../Icon.vue';
import DraggableModal from '../DraggableModal.vue';
import Lightbox from '../Lightbox.vue';
import PageDirtyService from '../../services/PageDirtyService';

// Interaces ---
// State ---
import { IMessageSignageUpdate, ISignageItem } from "../../store/interfaces/ISignage";
import SignageState, { IInitializeSignageItemPayload, ISignageState } from "../../store/modules/signage";
import SignagePreviewService from '../../services/SignagePreviewService';
import OrganizationState, { IOrganizationState } from "../../store/modules/organizations";
import UserState, { IUserState } from '../../store/modules/users';
import TierState, { ITierState } from '../../store/modules/tier';
import { extractYouTubeVideoId } from "./helper-functions/youtubeUrlHelper";

let signageItemState = {} as ISignageState;
let organizationState = {} as IOrganizationState;
let userState = {} as IUserState;
let tierState = {} as ITierState;

export default defineComponent({
  name: 'SignageDetailsComponent',
  data: ()=>({
    // FLOCK-6612: ticks when an async video poster capture lands so the
    // livePreviewLayoutData computed re-runs and the poster appears.
    queuedPosterRevision: 0 as number,
    signageItem: {} as ISignageItem,
    signageItemOriginal: {} as ISignageItem,
    initSignageItem: {} as ISignageItem,
    layoutDataEdit: {} as any,
    layoutDataOriginal: {} as any,
    selectedSignageItemPreview: null as any,
    signageItemPreviewDebounce: null as any,
    organization: null as any,
    youtubeUrlValid: true as boolean,

    // Boolean Flags
    // ==============
    isEditingItemName: false as boolean,
    signageItemLoading: true as boolean,
    isUpdateSignageItemLoading: false as boolean,
    isLoadingUpdatedPreview: true as boolean,
    isLightboxVisible: false as boolean,
    isPopoutPreviewVisible: false as boolean,

    // Icons
    // ======
    mdiFormatListText,
    mdiDragHorizontalVariant,
    mdiCrosshairsGps,
    mdiInformationVariant,
    mdiVideoImage,
    mdiViewCarousel,
    mdiBookOpen,
    mdiSpotlight,
    mdiSocialDistance6Feet,
    mdiFaceMask,
    mdiStarBox,
    mdiTextBoxPlus,
    mdiAccountGroup,
    mdiViewSplitVertical,
    mdiMap,
    mdiWeb,
    mdiHandWash,
    mdiDotsVertical,
    mdiDelete,
    mdiPencil,
    mdiMagnify,
    mdiChevronRight,
    mdiOpenInNew,
    mdiFullscreen,
  }),

  components: {
    DraggableModal,
    Lightbox,
    Icon,
    LiveTemplatePreview,

    // Layout Editors
    // ================
    detailedinformationalLayoutEditor: DetailedInformationalLayoutEditor,
    directoryLayoutEditor: DirectoryLayoutEditor,
    featureLayoutEditor: FeatureLayoutEditor,
    featuretwoLayoutEditor: FeatureTwoLayoutEditor,
    informationalLayoutEditor: InformationalLayoutEditor,
    largeheadlineinformationalLayoutEditor: LargeHeadlineInformationalLayoutEditor,
    mediaLayoutEditor: MediaLayoutEditor,
    multiitemmenuLayoutEditor: MultiItemMenuLayoutEditor,
    singleitemmenuLayoutEditor: SingleItemMenuLayoutEditor,
    spotlightLayoutEditor: SpotlightLayoutEditor,
    staticweburlLayoutEditor: StaticWebUrlLayoutEditor,
    wayfindingLayoutEditor: WayfindingLayoutEditor,
    weatherthreedayLayoutEditor: WeatherThreeDayLayoutEditor,
    weathertodayLayoutEditor: WeatherTodayLayoutEditor,
    mediaquadrantsLayoutEditor: MediaQuadrantsLayoutEditor,
    youtubevideoLayoutEditor: YoutubeUrlLayoutEditor,

    // Integration Layout Editors
    // ===========================
    canvaLayoutEditor: CanvaLayoutEditor,
    twitterLayoutEditor: TwitterLayoutEditor,
    googleslidesimportLayoutEditor: GoogleSlidesImportLayoutEditor,
    
    // Health and Safety Layout Editors
    // ======================
    covidmaskLayoutEditor: CovidMaskLayoutEditor, //to be removed
    'occupancy-healthLayoutEditor': OccupancyHealth,
    'occupancy-generalLayoutEditor': OccupancyGeneral,
    'health-tipsLayoutEditor': HealthTips,
    NonEditableLayoutEditor: NonEditableLayoutEditor
  },

  methods: {
    // FLOCK-6612: one object URL per queued File, cached on a non-reactive Map
    // (created()) so the computed above stays pure and URLs are not re-minted
    // per keystroke. Revoked in beforeUnmount.
    queuedFileUrlFor(file: File): string {
      const cache = (this as any).queuedFileUrls as Map<File, string>;
      if (!cache.has(file)) {
        cache.set(file, URL.createObjectURL(file));
      }
      return cache.get(file) as string;
    },

    // FLOCK-6612: capture a first-frame poster for queued video files so the
    // live preview can show something before the save-time upload generates
    // the real poster. Every failure path is silent by design: no poster
    // simply leaves the slot as it was.
    capturePostersForQueuedVideos() {
      const cache = (this as any).queuedFileUrls as Map<File, string>;
      const inFlight = (this as any).posterCapturesInFlight as Set<File>;
      for (const queued of this.fileUploadQueue) {
        if (!queued || !queued.file || !queued.file.type || !queued.file.type.startsWith('video')) { continue; }
        if (cache.has(queued.file) || inFlight.has(queued.file)) { continue; }
        inFlight.add(queued.file);
        this.generateVideoPoster(queued.file).then((posterUrl: string) => {
          cache.set(queued.file, posterUrl);
          this.queuedPosterRevision++;
        }).catch(() => { /* no poster: slot stays gray, same as before */ })
          .finally(() => { inFlight.delete(queued.file); });
      }
    },

    generateVideoPoster(file: File): Promise<string> {
      return new Promise<string>((resolve, reject) => {
        const videoUrl = URL.createObjectURL(file);
        const video = document.createElement('video');
        let settled = false;
        const finish = (err: any, url?: string) => {
          if (settled) { return; }
          settled = true;
          clearTimeout(timer);
          URL.revokeObjectURL(videoUrl);
          video.removeAttribute('src');
          if (url) { resolve(url); } else { reject(err); }
        };
        // Large files or undecodable codecs must never wedge the queue.
        const timer = setTimeout(() => finish(new Error('poster capture timeout')), 8000);
        video.muted = true;
        video.preload = 'auto';
        video.onloadeddata = () => {
          try { video.currentTime = 0.1; } catch (e) { finish(e); }
        };
        video.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx || !canvas.width) { return finish(new Error('no frame to capture')); }
            ctx.drawImage(video, 0, 0);
            canvas.toBlob((blob) => {
              if (blob) { finish(null, URL.createObjectURL(blob)); }
              else { finish(new Error('toBlob returned null')); }
            }, 'image/png');
          } catch (e) { finish(e); }
        };
        video.onerror = () => finish(new Error('video decode failed'));
        video.src = videoUrl;
      });
    },

    // FLOCK-6612: media urls are S3-signed with a 3600s expiry, so an edit
    // session longer than an hour watched its images go gray. Refresh ONLY
    // signageAssets (the urls live there); layoutData holds the user's
    // unsaved edits and is never touched.
    async refreshSignageAssetUrls() {
      if (!this.signageItem || !this.signageItem.id || !this.$route.params.listid) { return; }
      try {
        await signageItemState.fetchSignageForList({ signageListId: this.$route.params.listid as string, forceReload: true });
        const fresh = await signageItemState.signageItem(this.signageItem.id);
        if (fresh && fresh.signageAssets && fresh.signageAssets.length) {
          this.signageItem.signageAssets = fresh.signageAssets;
        }
      } catch (e) {
        // Next interval retries; a stale url is no worse than before.
      }
    },

    async loadSignage() {
      this.signageItemLoading = true;

      const layoutType = this.$route.params.layouttype as string;
      const listId = this.$route.params.listid as string;
      const signageId = this.$route.params.itemid as string;

      if(!listId) {
        this.$router.push({ path: '/signage' });
        return;
      }

      await signageItemState.fetchSignageForList({ signageListId: listId, forceReload: false });
      
      if(this.isCreatingNewSignageItem) {
        if(!layoutType) {
          this.$router.push({ path: `/signage/${listId}` });
          return;
        } 
        const initPayload: IInitializeSignageItemPayload = {
          signageListId: listId,
          layoutType: layoutType
        }
          
        const initResponse = await signageItemState.initializeSignageItem(initPayload);
        
        if(!initResponse.success) {
          ElMessage({
            message: `Signage item initialization failed`,
            type: 'error',
          });
            
          this.$router.push({ path: `/signage/${listId}` })
        }

        this.signageItem = initResponse.data as ISignageItem;
        this.initSignageItem = {...initResponse.data as ISignageItem};
        this.layoutDataEdit = this.signageItem.layoutData;
        this.layoutDataOriginal = this.signageItem.layoutData;
        this.signageItemLoading = false;
        // FLOCK-6612: live-preview templates skip the debounced server
        // preview whose success handler was the only thing clearing this
        // flag on create, leaving it stuck true — overlay never lifted and
        // Save stayed disabled (the flag also gates onSubmit).
        if(!this.shouldUpdatePreview || this.isLivePreviewSupported) {
          this.isLoadingUpdatedPreview = false;
        }
        return;
      }
      else {
        if(!signageId) {
            this.$router.push({ path: `/signage/${listId}` })
            return;
        }
          
        // load the signage item based off the URL
        this.signageItem = await signageItemState.signageItem(signageId);
        if(!this.signageItem) {
          ElMessage({
            message: `Signage item failed to load`,
            type: 'error',
          });

          this.$router.push({ path: `/signage/${listId}` })
          return;
        }
        this.layoutDataEdit = this.signageItem.layoutData;
        this.layoutDataOriginal = this.signageItem.layoutData;
        this.signageItemLoading = false;

        console.log('Signage Item last generated at: ', this.signageItem.generatedAt ?? 'NEVER');
          
        if(this.shouldUpdatePreview) {
          try {
              this.isLoadingUpdatedPreview = true;
              const updatedPreview = (await SignagePreviewService.updatePreview(this.signageItem as ISignageItem));
              this.isLoadingUpdatedPreview = false;
              this.$nextTick(() => {
                  this.selectedSignageItemPreview = updatedPreview ? updatedPreview.preview : null;
              });
          } catch(err) {
              // FLOCK-6612: was `= true`, which left the loading state stuck
              // after any preview failure — and onSubmit refuses to save
              // while isLoadingUpdatedPreview is set.
              this.isLoadingUpdatedPreview = false;
          }
        }
        else {
          this.isLoadingUpdatedPreview = false;
        }
      }
      if(signageId) {
        this.signageItemOriginal = await signageItemState.signageItem(signageId); 
      }
    },

    async changeDetection() {
      if(this.isImport && this.isCreatingNewSignageItem) {
        PageDirtyService.markPageClean();
        return;
      }
      let originalSignage;
      if(this.signageItem && this.signageItem.id) {
        originalSignage = await signageItemState.signageItem(this.signageItem.id) as any;
      }
       
      //we don't want to monitor the fields that the event emitter is updating that the user cannot update
      const origSignage = {...originalSignage} as any;
      delete origSignage.finalSignageAsset;
      delete origSignage.finalAssetGenerationError;
      delete origSignage.wipThumb;

      const editedSignage = {...this.signageItem} as any;
      delete editedSignage.finalSignageAsset;
      delete editedSignage.finalAssetGenerationError;
      delete editedSignage.wipThumb;
      
      PageDirtyService.monitorObjectSet([
            {defaultObject: origSignage, mutatedObject: editedSignage},
      ]);

      const queue = signageItemState.getFileUploadQueue;
      if(queue.length) {
        PageDirtyService.markPageDirty(); 
      }
    },

    async onSubmit(): Promise<void> {
      if(this.isLoadingUpdatedPreview || this.isUpdateSignageItemLoading) return;
      this.isUpdateSignageItemLoading = true;
      
      if ((this.isYouTubeVideo || this.signageItem.layoutType === 'youtubevideo') && !this.youtubeUrlValid) {
        ElMessage({
          message: 'Please enter a valid YouTube URL',
          type: 'error',
        });
        this.isUpdateSignageItemLoading = false;
        return;
      }
      
      if (this.isYouTubeVideo && this.iframeEmbeddedUrl || this.signageItem.layoutType === 'youtubevideo' && this.iframeEmbeddedUrl) {
        const videoId = this.iframeEmbeddedUrl.split('/').pop();
        const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;

        try {
          const response = await fetch(oembedUrl);
          const jsonResponse = await response.json();
         
          if (!response.ok || response.status != 200) {
            throw new Error('Video is not playable or does not exist');
          }

          // Proceed to save the video ID
          this.signageItem.layoutData = {
            ...this.signageItem.layoutData,
            videoId,
            videoTitle: jsonResponse.title,
            videoAuthor: jsonResponse.author_name
          };
        } catch (err: any) {
           ElMessage({
            message: 'Invalid or private YouTube video',
            type: 'error',
          });
          return;
        }
      }

      // FLOCK-6612: live-preview templates skip the per-edit server renders,
      // so render once here to refresh the thumbnail that persists with the
      // save (the list view shows it while the final asset builds). A failed
      // or slow render keeps the previous thumb and never blocks the save.
      if (this.isLivePreviewSupported) {
        try {
          const savePreview = await SignagePreviewService.updatePreview(this.signageItem as ISignageItem) as any;
          if (savePreview) {
            this.signageItem.wipThumb = savePreview.thumb;
            this.selectedSignageItemPreview = savePreview.preview;
          }
        } catch (err) {
          console.log('save-time preview render failed, keeping previous thumbnail', err);
        }
      }

      const payload = {
        signageListId: this.$route.params.listid as string,
        signageItem: this.signageItem,
        shouldUpdatePreview: this.shouldUpdatePreview
      } as any;
      let metaSaveResults = null as any;
      let saveResponse = null as any;
      
      if(!this.isCreatingNewSignageItem) {
        metaSaveResults = await signageItemState.updateSignageItemMeta(this.signageItem);
        if(!metaSaveResults.success) {
          ElMessage({
            message: 'Error saving signage item',
            type: 'error',
          });
          this.isUpdateSignageItemLoading = false;
          return;
        }
      }

      if(this.isCreatingNewSignageItem || (JSON.stringify(this.signageItem.layoutData) != JSON.stringify(this.layoutDataOriginal) || this.fileUploadQueue.length || this.signageItem.refreshedLayout || this.signageItem.layoutType == 'mediaquadrants')) {
        saveResponse = await signageItemState.saveSignageItem(payload) as any;
        if(!saveResponse.success || !saveResponse.data.id) {
          ElMessage({
            message: 'Error saving signage item',
            type: 'error'
          });
          this.isUpdateSignageItemLoading = false;
          return;
        }
      
        if(this.isCreatingNewSignageItem) {
          const newItem = await signageItemState.signageItem(saveResponse.data.id);
          if(newItem) {
            PageDirtyService.markPageClean();
            await tierState.fetchAddSignageItemCheck(true);
            this.$router.push({ path: `/signage/${payload.signageListId}/${newItem.id}` });
          }
          else {
            ElMessage({
              message: `Signage item failed to load`,
              type: 'error',
            });
            this.$router.push({ path: `/signage/${payload.signageListId}` })
            this.isUpdateSignageItemLoading = false;
            return;
          }
              
        }

        this.loadSignage();

        if((this.signageItem.layoutType === 'media' || this.signageItem.layoutType === 'canva' || this.signageItem.layoutType === 'googleslidesimport') && saveResponse.data && saveResponse.data.finalSignageAsset){
          this.signageItem.finalSignageAsset = saveResponse.data.finalSignageAsset;
        }
        
        this.signageItemLoading = false;
      }

      this.selectedSignageItemPreview = this.signageItem.wipThumb;
      await organizationState.fetchSignageAssetQuota();
      this.isUpdateSignageItemLoading = false;
      ElMessage({
        message: 'Signage item saved',
        type: 'success',
      });

      PageDirtyService.markPageClean();
      signageItemState.clearFileQueue();
    },

    toggleEditListNameInput(){ 
      this.isEditingItemName = true;
      this.$nextTick(()=> {
        (this as any).$refs['editItemNameInput'].focus();
      });
    },

    updatePlaySound(playSound: boolean) {
      this.signageItem.playSound = playSound;
      this.changeDetection();
    },

    updateLayoutData(componentUpdate: any) {
      this.signageItem.layoutData = componentUpdate.updatedLayoutData;
      this.changeDetection();

      // FLOCK-6612: live-preview templates render client-side on every edit,
      // so the debounced server render is skipped entirely; one server
      // render happens at save to refresh the persisted thumbnail.
      if(componentUpdate.shouldUpdatePreview && !this.isLivePreviewSupported) {
        if(this.signageItemPreviewDebounce) clearTimeout(this.signageItemPreviewDebounce);
  
        // Only update the preview after 1500ms
        this.signageItemPreviewDebounce = setTimeout(()=> {
          this.isLoadingUpdatedPreview = true;
          SignagePreviewService.updatePreview(this.signageItem).then((resp: any)=> {
            if(resp) {
              this.isLoadingUpdatedPreview = false;
              this.selectedSignageItemPreview = resp.preview;
              // FLOCK-6612: carry the fresh thumb on the item so the next
              // save persists it and the list view shows it while the final
              // asset builds. changeDetection excludes wipThumb, so this
              // does not mark the page dirty. A null resp means this request
              // was superseded by a newer edit; the newer one drives the UI.
              this.signageItem.wipThumb = resp.thumb;
            }
          }).catch(() => {
            // FLOCK-6612: a failed or timed-out preview must not wedge the
            // loading state; onSubmit refuses to save while it is set. The
            // last good preview image stays up.
            this.isLoadingUpdatedPreview = false;
          });
        }, 1500);
      }
    },

    updateSignageFromMessage() {
      if(this.signageMessageUpdate) {
        this.signageItem.finalSignageAsset = this.signageMessageUpdate.finalSignageAsset;
        this.signageItem.finalAssetGenerationError = this.signageMessageUpdate.finalAssetGenerationError;
        this.signageItem.wipThumb = this.signageMessageUpdate.wipThumb;
      }
    },

    // difference(obj1: any, obj2: any): boolean {
    //   let keyFound = false;
    //   if(obj1 && obj2) {
    //   Object.keys(obj1).forEach(key => {
    //       if(obj1[key] !== obj2[key]){
    //         console.log('key difference', key);
    //         return keyFound = true;
    //       }
    //   });
    //   return keyFound;
    // }
  },

  props: ['isCreatingNewSignageItem'],

  computed: {
     iframeEmbeddedUrl(): string | null {
      if (!this.signageItem) return null;
      if(this.isYouTubeVideo){
        const layoutData = this.signageItem.layoutData as { url?: string };
        if(!layoutData || !layoutData.url) return null;
        const videoId = extractYouTubeVideoId(layoutData.url)
        if(!videoId) return null;
        return `https://www.youtube.com/embed/${videoId}`
      }
      else if(!this.isYouTubeVideo && this.signageItem.layoutData as { videoId?: string }){
        const layoutData = this.signageItem.layoutData as { videoId?: string };
        return `https://www.youtube.com/embed/${layoutData.videoId}`
      }
      return null;
    },
    isYouTubeVideo(): boolean {
      let layoutType = this.$route.params.layouttype as string;
      if(!layoutType) layoutType = this.signageItem.layoutType;
      if(!layoutType) return false;
      return layoutType.toLowerCase() === 'youtubevideo';
    },

    signageItemEditorComponent(): string {
      // Returns a string that will map the selected signage item to
      // the corresponding editor component. Ex: media -> MediaLayoutEditor
      // This is used for the dynamic <component /> element 
      const layoutType = this.signageItem ? this.signageItem.layoutType : '';
      
      // Check if this is an old layout type that doesn't have an editor
      const oldLayoutTypes = ['covid-occupancy', 'covidoccupancytwo', 'covid-safety', 'covidmask'];
      if (oldLayoutTypes.includes(layoutType)) {
        return 'NonEditableLayoutEditor';
      }
      
      return layoutType + "LayoutEditor";
    },

    fileUploadQueue(): any[] {
        const queue = signageItemState.getFileUploadQueue;
        if(queue.length) {
          this.changeDetection();
        }
        return queue;
    },

    signageItemPreviewImage(): string | null {
      if((this.isImport && !this.signageItem.finalSignageAsset) || (this.signageItem.refreshedLayout && this.signageItem.finalAssetGenerationError || (this.signageItem.finalSignageAsset && this.signageItem.finalSignageAsset.remoteUploadPending)) ){
        return 'images/signage-layout-images/staticweburlloading.png';
      }
      if (this.signageItem.layoutType !== 'media' && this.signageItem.layoutType !== 'canva'  && this.signageItem.layoutType !== 'googleslidesimport' && this.selectedSignageItemPreview) {
        return `data:image/png;base64,${ this.selectedSignageItemPreview }`;
      }
      if (this.signageItem.layoutType == 'media' || this.signageItem.layoutType == 'canva' || this.signageItem.layoutType == 'googleslidesimport') {
        if(this.signageItem.finalSignageAsset && this.signageItem.finalSignageAsset.assetType == 'video') {
          return this.signageItem.finalSignageAsset.thumbUrl; 
        }
        if(this.signageItem.signageAssets && this.signageItem.signageAssets.length) {
          const assetFiles = this.signageItem.signageAssets.flatMap((x: any) => x.assetFiles);
          if(assetFiles.length) {   
            return assetFiles[0].url;
          }
        }
      }
      if(this.signageItem.finalSignageAsset && this.signageItem.finalSignageAsset.thumbUrl && !this.signageItem.finalSignageAsset.thumbUrl.includes('undefined')) {
        return this.signageItem.finalSignageAsset.thumbUrl;      
      }
      if (this.signageItem && this.signageItem.wipThumb) {
        return `data:image/png;base64,${this.signageItem.wipThumb}`;
      } else {
        if(this.signageItem.layoutType) {
          return `images/signage-layout-images/${this.signageItem.layoutType}.png`;
        }
        else {
          return null;
        }
      }
    },
      
    hasError(): boolean {
      if((this.signageItem as any).finalAssetGenerationError) {
        return true;
      }
      else {
        return false;
      }
    },

    isSavingRequired(): boolean {
      if (this.signageItem.refreshedLayout && JSON.stringify(this.signageItem.layoutData) != JSON.stringify(this.layoutDataOriginal)){
      return true;
      }
      return false;
    },

    isValidMediaLayout: function() {
      if (this.signageItem.layoutType === 'media') {
        if((this.isCreatingNewSignageItem && this.fileUploadQueue.length == 0) ||
          (this.fileUploadQueue.length == 0 && 
          this.signageItem.playSound === this.signageItemOriginal.playSound &&
          this.signageItem.name === this.signageItemOriginal.name)) {
            return false;
          }
      }
      return true;
    },

    showPreview(): boolean {
      if((this.signageItem.layoutType == "media" || this.signageItem.layoutType == "canva" || this.signageItem.layoutType == "googleslidesimport" )&& this.isCreatingNewSignageItem) {
        return false;
      }
      return true;
    },

    isLivePreviewSupported(): boolean {
      // FLOCK-6612: rollout switch for client-side live preview. Every template
      // listed here renders client-side in the editor and tracks typing without
      // a server render; anything absent keeps the server-rendered preview.
      //
      // Deliberately excluded:
      //  - weathertoday / weatherthreeday: the external weather fetch is not
      //    wired into live mode yet, so the preview would render empty.
      //  - staticweburl / twitter: third-party embeds the portal's frame-src
      //    CSP will not load inside our own iframe.
      //  - media / mediaquadrants / canva: no live mode, these are asset-backed
      //    rather than layout-data-driven.
      //  - covid-occupancy / covidoccupancytwo / covid-safety / covidmask: the
      //    legacy names are not editable at all (see signageItemEditorComponent),
      //    so a live preview has nothing to track. Their current equivalents
      //    (occupancy-health, occupancy-general, health-tips) are enabled.
      const supported = [
        'singleitemmenu',
        'multiitemmenu',
        'informational',
        'detailedinformational',
        'largeheadlineinformational',
        'spotlight',
        'directory',
        'feature',
        'featuretwo',
        'wayfinding',
        'occupancy-health',
        'occupancy-general',
        'health-tips'
      ];
      return !!(this.signageItem && supported.includes(this.signageItem.layoutType as string));
    },

    livePreviewLayoutData(): any {
      // FLOCK-6612: the live preview posts layoutData into the template iframe
      // verbatim, but the media *_url values stored there are the ~200px S3
      // _thumb derivatives (previewTemplate renews them server-side with
      // asset.thumbUrl). Stretched across the 3840x2160 live canvas they render
      // blurry, and a file that is still in the upload queue has no *_url at
      // all, so the template painted url(undefined) — the gray box QE reported.
      // The server-render path is unaffected: previewTemplate re-resolves media
      // urls itself and ignores whatever the editor sends in *_url.
      //
      // This computed feeds the preview a corrected CLONE; the persisted
      // signageItem.layoutData is never touched.
      const layoutData = JSON.parse(JSON.stringify(this.signageItem.layoutData || {}));
      if (!this.isLivePreviewSupported || !layoutData.media) {
        return layoutData;
      }
      const media = layoutData.media;

      // Saved assets: swap the thumb for the full-size asset file url the
      // editor already holds. Videos keep the thumb (a generated poster
      // frame): a CSS background cannot render an mp4.
      const signageAssets = (this.signageItem.signageAssets || []) as any[];
      for (const key of Object.keys(media)) {
        if (key.endsWith('_url') || !media[key]) { continue; }
        const asset = signageAssets.find((a: any) => a && a.id === media[key]);
        if (!asset || asset.assetType === 'video') { continue; }
        const assetFiles = asset.assetFiles || [];
        if (assetFiles.length && assetFiles[0].url) {
          media[`${key}_url`] = assetFiles[0].url;
        }
      }

      // Files still in the upload queue have no asset yet, so nothing
      // server-side can provide a url. A local object URL shows the picked
      // image immediately (blob urls are origin-scoped, so the same-origin
      // iframe can load them). Queued videos get a poster frame captured
      // client-side (see the fileUploadQueue watcher); until that async
      // capture lands the slot stays as-is.
      void this.queuedPosterRevision;
      const queuedUrls = (this as any).queuedFileUrls as Map<File, string>;
      for (const queued of this.fileUploadQueue) {
        if (!queued || !queued.layoutProperty || !queued.file) { continue; }
        if (queued.file.type && queued.file.type.startsWith('video')) {
          const poster = queuedUrls.get(queued.file);
          if (poster) { media[`${queued.layoutProperty}_url`] = poster; }
          continue;
        }
        media[`${queued.layoutProperty}_url`] = this.queuedFileUrlFor(queued.file);
      }

      return layoutData;
    },

    shouldUpdatePreview(): boolean {
      if(this.signageItem && JSON.stringify(this.signageItem) !== '{}' && !this.signageItem.refreshedLayout && this.signageItem.layoutType !== "media" && this.signageItem.layoutType !== "mediaquadrants" && this.signageItem.layoutType !== "canva" && this.signageItem.layoutType !== "googleslidesimport" && this.signageItem.layoutType !== "youtubevideo") {
        return true;
      }
      return false;
    },

    finalAssetGenerationErrorMessage(): string {
      if((this.signageItem.layoutType === "staticweburl" || this.signageItem.layoutType === "twitter" ||  this.signageItem.layoutType === "googleslidesimport") && this.signageItem.finalAssetGenerationError)
      {
        const detail = this.signageItem.finalAssetGenerationError.message;
        // Keep this reassuring rather than alarming: a placeholder still renders,
        // and re-saving re-queues the item for generation.
        return detail
          ? `We couldn't generate a preview for this content yet (${detail}). Check the source and save again to retry.`
          : "We couldn't generate a preview for this content yet. Check the source and save again to retry.";
      }
      return '';
    },

    signageMessageUpdate() {  
      if(this.signageItem.id) {
        return signageItemState.messageSignageUpdateBySignageId(this.signageItem.id);
      }
      return {} as IMessageSignageUpdate;
    },

    isLayoutDisbabled() {
      if(this.signageItem.layoutType == 'canva' && !tierState.orgTier.signageCanvaEnabled) {
          return true;
      }
      if(this.signageItem.layoutType == 'twitter' && !tierState.orgTier.signageTwitterEnabled) {
          return true;
      }
      return false;
    },

    featureWarningText(): string {
      const name = this.signageItem.layoutType.charAt(0).toUpperCase() + this.signageItem.layoutType.slice(1);
      return `${name} is not enabled for your organization.`;
    },

    isImport(): boolean {
        if(this.signageItem.layoutType && (this.signageItem.layoutType === 'googleslidesimport' || this.signageItem.layoutType === 'canva')) {
          return true;
        }

        return false;
    },
  },
    inject: [
        'windowWidth'
    ],


  beforeUnmount() {
    if ((this as any).assetUrlRefreshTimer) {
      clearInterval((this as any).assetUrlRefreshTimer);
    }
    // FLOCK-6612: release the object URLs minted for queued upload previews
    // (including captured video posters, which live in the same cache).
    const cache = (this as any).queuedFileUrls as Map<File, string> | undefined;
    if (cache) {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    }
  },

  async created() {
    // Non-reactive on purpose: a reactive Map written from inside the
    // livePreviewLayoutData computed would invalidate its own dependency.
    (this as any).queuedFileUrls = new Map();
    (this as any).posterCapturesInFlight = new Set();

    // FLOCK-6612: re-sign media urls before the 3600s S3 signature expires.
    // 45 minutes leaves comfortable margin inside the signing window.
    (this as any).assetUrlRefreshTimer = setInterval(() => { this.refreshSignageAssetUrls(); }, 45 * 60 * 1000);

    signageItemState = getModule(SignageState);
    organizationState = getModule(OrganizationState);
    userState = getModule(UserState);
    tierState = getModule(TierState)
    
    signageItemState.clearFileQueue();

    await userState.fetchMe();
    await tierState.fetchTier();

    if( userState.me) {
        this.organization = userState.me.organization;
    }

    await this.loadSignage();
  },

  watch: {
    fileUploadQueue: {
      handler() {
        // FLOCK-6612: kick off poster capture for any newly queued video.
        this.capturePostersForQueuedVideos();
      }
    },

    'signageItem.name': {
      handler(){
        this.changeDetection();
      }
    },

    'signageMessageUpdate': {
      handler(newMessage, oldMessage){
        if(JSON.stringify(newMessage) !== JSON.stringify(oldMessage)) {
          this.updateSignageFromMessage();
        }  
      },
      deep: true
    }
  }
});
